import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import store, { waitForUser } from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { waitForElementsToLoad } from '../../../common/utils/delay-util';
import emit from '../../../common/events';
import { loading } from '../../../common/constants/api-constants';
import { getUserDataForUserAnalytics, getUserPageView, isUserInstructor } from '../../user-profile/user-profile.selectors';
import { StoreState } from '../../../store/store.interface';
import { booleanConverter } from '../../../common/converters';
import { PayPalMessageFontSize } from './interfaces/paypal.interface';

/**
 * Component to show Paypal messages.
 * This component is rendered without shadow DOM and needs to be in the main DOM of the page to work.
 */
@customElement(`mhecomm-tokenless-paypal-messages`)
export class TokenlessPaypalMessages extends connect(store)(BaseComponent) {
  /**
   * Flag to hide the Paypal messages for instructor user type.
   */
  @property({ converter: booleanConverter })
  hideForInstructor = false;

  /**
   * Sets the font size of the text for text layout messages.
   */
  @property({ type: Number })
  size: PayPalMessageFontSize = 12;

  /**
   * Informs PayPal's analytics and system logging of the type of e-commerce page on which the message is placed.
   */
  @property()
  placement: 'home' | 'category' | 'cart' | 'product' | 'payment' = 'cart';

  /**
   * Total amount of the order.
   */
  @property()
  totalAmount;

  @property()
  isbn10;

  /**
   * Duration of the product.
   */
  @property()
  duration;

  /**
   * Duration text of the product.
   */
  @property()
  durationString;

  /**
   * type text of the product.
   */
  @property({ type: String })
  pdpType = '';

  /**
   * fomrat text of the product.
   */
  @property({ type: String })
  productFormat = '';

  @state()
  showPayPalMessage = true;

  /**
   * Flag to indicate component is loading
   */
  @state()
  private isLoading = true;

  /**
   * Remove shadow DOM
   * @returns
   */
  createRenderRoot() {
    return this;
  }

  renderComp() {
    return html`${this.isLoading
      ? html`${loading()}`
      : html`${this.showPayPalMessage ? html`<div id="paypal-message-${this.dataset.isbn}-container"></div>` : nothing}`}`;
  }

  async connectedCallback() {
    super.connectedCallback();
    if(this.hideForInstructor){
      this.showPayPalMessage = !isUserInstructor(store.getState() as StoreState);
    }
    await waitForUser();
    this.isLoading = false;
    waitForElementsToLoad(this, [`#paypal-message-${this.dataset.isbn}-container`]).then(() =>
      window.paypal
        .Messages({
          amount: this.totalAmount,
          placement: this.placement,
          style: {
            layout: 'text',
            text: {
              size: this.size,
            },
            logo: {
              type: 'none',
            },
          },
          onClick: () => {
            emit({
              element: this,
              name: 'click',
              detail: {
                comp: 'text',
                label: 'PayPal Learn More',
                url: window.location.href,
                duration: this.duration > 0 ? this.durationString : 'Product without duration',
                price: this.totalAmount,
                isbn13: this.dataset.isbn,
                isbn10: this.isbn10,
                pdpType: this.pdpType,
                productFormat: this.productFormat,
                pdpVersion: getUserPageView(store.getState() as StoreState),
                user: getUserDataForUserAnalytics(store.getState() as StoreState),
              },
            });
          }
        })
        .render(`#paypal-message-${this.dataset.isbn}-container`)
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-tokenless-paypal-messages': TokenlessPaypalMessages;
  }
  interface Window {
    paypal: any;
  }
}
